/**
 * Agents API
 *
 * Typed wrappers for agent fleet and live session endpoints.
 * Consumes the KodeonApiClient singleton.
 *
 * Architecture: Layer 3 (Feature Module)
 * - Calls KodeonApiClient from Layer 2
 * - Types defined in features/agents/types.ts
 * - Consumed by agents store and components
 */

import { api } from '@/lib/api'
import type {
  AgentsViewAgent,
  AgentDetail,
  AgentBlueprint,
  AgentConfig,
  LiveSession,
  SessionEvent,
  PlaygroundSession,

  PlaygroundSessionSaveRequest,
  PlaygroundSessionForkRequest,
  PlaygroundSessionListResponse,
  PlaygroundStreamEvent,
} from './types'

/**
 * Fetch all agents.
 *
 * Endpoint: GET /v1/agents
 * Backend returns either {agents: AgentMetadata[]} (canonical) or AgentsViewAgent[] (legacy).
 * We tolerate both shapes and map AgentMetadata to AgentsViewAgent with sensible defaults
 * for the rich-view fields that the basic registry endpoint doesn't include.
 *
 * @returns Array of agents with queue counts, memory health, and stats
 */
export async function fetchAgents(): Promise<AgentsViewAgent[]> {
  // Note: trailing slash matters - the AGENTS service registers `@router.get("/")`
  // with prefix `/v1/agents`, so calling without trailing slash triggers a 307
  // redirect to the upstream URL directly (which fails auth). Using `/v1/agents/`
  // would also miss the gateway route. Best fix is at the service.
  const response = await api.get<unknown>('/v1/agents')

  // Backend canonical shape: {agents: AgentMetadata[]}
  if (response && typeof response === 'object' && 'agents' in response) {
    const agents = (response as { agents?: unknown[] }).agents ?? []
    return agents.map(mapAgentMetadataToView)
  }

  // Legacy shape: array directly
  if (Array.isArray(response)) {
    return response as AgentsViewAgent[]
  }

  return []
}

/**
 * Map the backend's basic AgentMetadata shape to the Stream rich AgentsViewAgent shape,
 * filling rich fields with safe defaults. Stats are loaded separately.
 */
function mapAgentMetadataToView(meta: unknown): AgentsViewAgent {
  const m = (meta ?? {}) as Record<string, unknown>
  return {
    agent_id: String(m.agent_id ?? m.id ?? ''),
    display_name: String(m.display_name ?? m.name ?? m.agent_id ?? ''),
    description: String(m.description ?? ''),
    domain: (m.domain as AgentsViewAgent['domain']) ?? 'general',
    status: (m.status as AgentsViewAgent['status']) ?? 'active',
    queueCount: typeof m.queueCount === 'number' ? m.queueCount : 0,
    memoryHealth: (m.memoryHealth as AgentsViewAgent['memoryHealth']) ?? null,
    todayStats: (m.todayStats as AgentsViewAgent['todayStats']) ?? null,
  }
}

/**
 * Fetch agent configuration (used as detail view).
 *
 * Endpoint: GET /v1/agents/{agent_id}/config
 * @param agentId - Agent ID to fetch
 * @returns Agent configuration and metadata
 */
export async function fetchAgentDetail(agentId: string): Promise<AgentDetail> {
  return api.get<AgentDetail>(`/v1/agents/${agentId}/config`)
}

/**
 * Fetch complete agent blueprint (full identity introspection).
 *
 * Endpoint: GET /v1/agents/{agent_id}/blueprint
 * Returns system instruction, prompt layers, tools, governance tier, etc.
 *
 * @param agentId - Agent ID to introspect
 * @param includeLayerContent - If true, include full text for all prompt layers
 * @returns Complete agent blueprint
 */
export async function fetchAgentBlueprint(
  agentId: string,
  includeLayerContent = false,
): Promise<AgentBlueprint> {
  const params = includeLayerContent ? '?include_layer_content=true' : ''
  return api.get<AgentBlueprint>(`/v1/agents/${agentId}/blueprint${params}`)
}

/**
 * Fetch live sessions.
 *
 * Endpoint: GET /v1/admin/agents/active-sessions
 * The backend returns an envelope: `{active_sessions: [...], total_active, as_of}`.
 * We unwrap here so the store receives a flat LiveSession[].
 *
 * @returns Array of active agent sessions with status and activity
 */
export async function fetchLiveSessions(): Promise<LiveSession[]> {
  const envelope = await api.get<{ active_sessions: LiveSession[] }>('/v1/admin/agents/active-sessions')
  return envelope?.active_sessions ?? []
}

/**
 * Backend SessionLiveState snapshot shape
 * (returned by the monitoring SSE stream's "event" payloads).
 */
interface SessionLiveStateSnapshot {
  agent_id: string
  session_id: string
  status: string
  started_at: string
  last_activity_at: string
  current_activity: string
  trigger_summary: string
  pending_approval_id: string | null
}

/**
 * Map a backend SessionLiveState snapshot into a SessionEvent so the
 * store and LiveSessionDetail renderer can consume it without changes.
 *
 * The monitoring stream emits state *snapshots* (status + activity),
 * not discrete chat events (messages/tool_calls). We normalise the
 * shape here to keep the mapping at the API boundary per ARCHITECTURE.md §L3.
 */
function toSessionEvent(snapshot: SessionLiveStateSnapshot): SessionEvent {
  // Derive a human-readable type from the session status.
  const typeMap: Record<string, SessionEvent['type']> = {
    thinking: 'message',
    generating: 'message',
    tool_call: 'tool_call',
    waiting_approval: 'tool_call',
  }

  return {
    id: `${snapshot.session_id}_${snapshot.last_activity_at}`,
    type: typeMap[snapshot.status] ?? 'message',
    timestamp: snapshot.last_activity_at || snapshot.started_at,
    content: snapshot.current_activity || `Status: ${snapshot.status}`,
  }
}

/**
 * Subscribe to live session state updates (passive monitoring).
 *
 * Endpoint: GET /v1/agents/{agent_id}/sessions/{session_id}/stream
 *
 * Opens a GET-based SSE connection to the new monitoring endpoint.
 * Calls onEvent for each state-change event received from the server.
 * The stream is terminated when the provided AbortSignal is aborted
 * (e.g. when unsubscribeFromSession is called in the store).
 *
 * @param agentId - Agent ID owning the session
 * @param sessionId - Session ID to monitor
 * @param onEvent - Callback invoked for each incoming SessionEvent
 * @param signal - AbortSignal to cancel the stream
 */
export function subscribeToSessionEvents(
  agentId: string,
  sessionId: string,
  onEvent: (event: SessionEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  return api.subscribe<SessionLiveStateSnapshot>(
    `/v1/agents/${agentId}/sessions/${sessionId}/stream`,
    (snapshot) => {
      onEvent(toSessionEvent(snapshot))
    },
    (err) => {
      console.error(`[agentsApi] Session stream error (${agentId}/${sessionId}):`, err)
    },
    signal,
  )
}

/**
 * Save playground session.
 *
 * Endpoint: POST /v1/admin/agents/playground/sessions
 * @param request - Session save request with agent_id, messages, config, etc.
 * @returns Saved session with server-issued ID
 */
export async function savePlaygroundSession(
  request: PlaygroundSessionSaveRequest,
): Promise<PlaygroundSession> {
  return api.post<PlaygroundSession>('/v1/admin/agents/playground/sessions', request)
}

/**
 * List saved playground sessions.
 *
 * Endpoint: GET /v1/admin/agents/playground/sessions
 * @returns List of saved sessions scoped to the authenticated operator
 */
export async function listSavedPlaygroundSessions(): Promise<PlaygroundSessionListResponse> {
  return api.get<PlaygroundSessionListResponse>('/v1/admin/agents/playground/sessions')
}

/**
 * Load playground session.
 *
 * Endpoint: GET /v1/admin/agents/playground/sessions/{session_id}
 * @param sessionId - Session ID to load
 * @returns Full session including messages and config
 */
export async function loadPlaygroundSession(sessionId: string): Promise<PlaygroundSession> {
  return api.get<PlaygroundSession>(`/v1/admin/agents/playground/sessions/${sessionId}`)
}

/**
 * Fork playground session.
 *
 * Endpoint: POST /v1/admin/agents/playground/sessions/{session_id}/fork
 * @param sessionId - Session ID to fork from
 * @param request - Fork request with optional up_to_turn and title
 * @returns New forked session with new ID
 */
export async function forkPlaygroundSession(
  sessionId: string,
  request: PlaygroundSessionForkRequest,
): Promise<PlaygroundSession> {
  return api.post<PlaygroundSession>(
    `/v1/admin/agents/playground/sessions/${sessionId}/fork`,
    request,
  )
}

/**
 * Delete playground session.
 *
 * Endpoint: DELETE /v1/admin/agents/playground/sessions/{session_id}
 * @param sessionId - Session ID to delete
 */
export async function deletePlaygroundSession(sessionId: string): Promise<void> {
  await api.delete(`/v1/admin/agents/playground/sessions/${sessionId}`)
}

/**
 * Fetch agent configuration for playground.
 *
 * Endpoint: GET /v1/agents/{agent_id}/playground/config
 * @param agentId - Agent ID to fetch configuration for
 * @returns Agent configuration with tools, model, and warnings
 */
export async function fetchAgentConfig(agentId: string): Promise<AgentConfig> {
  const response = await api.get<any>(`/v1/agents/${agentId}/playground/config`)

  // Map backend snake_case to frontend camelCase.
  // Be defensive: if the backend omits an optional field (tools, warnings,
  // rate_limit) we want to render the page with sensible defaults rather than
  // throwing a TypeError that ends in a blank screen.
  const tools = Array.isArray(response?.tools) ? response.tools : []

  return {
    id: response?.id ?? response?.agent_id ?? agentId,
    agent_id: response?.agent_id ?? agentId,
    display_name: response?.display_name ?? agentId,
    model: response?.model ?? '',
    systemPrompt: response?.system_prompt_summary ?? '',
    defaultTemperature: 0.7, // Default, can be made configurable later
    availableTools: tools.map((t: any) => ({
      name: t?.name ?? '',
      description: t?.description ?? '',
      requires_approval: !!t?.requires_approval,
      blocked: !!t?.blocked,
    })),
    playgroundWarnings: response?.playground_warnings,
    rateLimit: response?.rate_limit,
  }
}

/**
 * Stream agent message via SSE.
 *
 * Endpoint: POST /v1/agents/{agent_id}/playground/stream
 * @param agentId - Agent ID to send message to
 * @param message - User message text
 * @param options - Configuration options
 * @param onEvent - Callback for each SSE event
 * @param signal - AbortSignal to cancel stream
 */
export async function streamAgentMessage(
  agentId: string,
  message: string,
  options: {
    sessionId?: string
    systemPromptOverride?: string
    enabledTools: string[]
    temperature: number
  },
  onEvent: (event: PlaygroundStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const payload = {
    session_id: options.sessionId,
    message,
    history: [], // Can be populated from store if needed
    context: {
      agent_overrides: {
        system_prompt: options.systemPromptOverride,
        enabled_tools: options.enabledTools,
        temperature: options.temperature,
      },
    },
  }

  // Use POST-based SSE subscription (same pattern as session event streaming)
  return api.streamPost<any>(
    `/v1/agents/${agentId}/playground/stream`,
    payload,
    (data) => {
      // Backend sends wrapped events: {type: "<type>", data: {...}}
      // Extract and forward to callback. Each event carries `data.author`
      // (the ID of the agent that produced it), which we propagate so the
      // store can track sub-agent activity inline.
      if (data.type && data.data) {
        switch (data.type) {
          case 'token':
            onEvent({
              type: 'token',
              content: data.data.content,
              author: data.data.author,
            })
            break
          case 'thought':
            onEvent({
              type: 'thought',
              content: data.data.content,
              author: data.data.author,
            })
            break
          case 'tool_call':
            onEvent({
              type: 'tool_call_start',
              toolCall: {
                id: data.data.id,
                tool: data.data.tool,
                input: data.data.input,
                author: data.data.author,
              },
            })
            break
          case 'tool_result':
            onEvent({
              type: 'tool_call_end',
              toolCallId: data.data.tool_id,
              tool: data.data.tool,
              output: data.data.output,
              author: data.data.author,
              durationMs: data.data.duration_ms,
              cost: data.data.cost,
            })
            break
          case 'sub_agent':
            onEvent({
              type: 'sub_agent',
              from: data.data.from,
              to: data.data.to,
            })
            break
          case 'final':
            onEvent({
              type: 'message_complete',
              message: {
                content: data.data.content,
                timestamp: new Date().toISOString(),
                author: data.data.author,
              },
            })
            break
          case 'error':
            onEvent({ type: 'error', error: data.data.error })
            break
        }
      }
    },
    (err) => {
      console.error(`[agentsApi] Stream error (${agentId}):`, err)
    },
    signal,
  )
}
